import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2,
  Download,
  FileText,
  Filter,
  PlayCircle,
  Search,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import type { CrmDataProvider } from "../../providers/types";

type QueueRow = {
  id: number;
  dc_id: number;
  dc_reference: string;
  challan_date: string;
  company_id: number | null;
  customer_name: string | null;
  customer_state: string | null;
  project_id: number | null;
  pi_id: number | null;
  dc_status: string;
  total_quantity: number;
  item_count: number;
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export const AutoInvoicingPage = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [selected, setSelected] = useState<number[]>([]);
  const [busyIds, setBusyIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetList<QueueRow>("dispatch_to_invoice_queue", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "challan_date", order: "DESC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? (row.customer_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
            row.dc_reference.toLowerCase().includes(search.toLowerCase())
          : true,
      ),
    [data, search],
  );

  const toggleAll = () =>
    setSelected(
      selected.length === rows.length ? [] : rows.map((r) => r.dc_id),
    );
  const toggleOne = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const generate = async (dcIds: number[]) => {
    setBusyIds((p) => [...p, ...dcIds]);
    try {
      const result = await dataProvider.autoInvoiceFromDC(dcIds);
      const invoiced = result.invoiceIds.length;
      const failed = result.errors.length;
      if (invoiced > 0) {
        notify(`Generated ${invoiced} invoice(s).`, { type: "success" });
      }
      if (failed > 0) {
        notify(
          `${failed} dispatch(es) could not be invoiced. See console for details.`,
          { type: "warning" },
        );
        console.warn("[auto-invoice] errors", result.errors);
      }
      refresh();
      setSelected([]);
    } catch (error) {
      console.error(error);
      notify(`Failed to generate invoices: ${(error as Error).message}`, {
        type: "error",
      });
    } finally {
      setBusyIds((p) => p.filter((x) => !dcIds.includes(x)));
    }
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Auto Invoice from Dispatch"
        description="Sales invoices generated automatically from dispatched delivery challans."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-l-4 border-l-primary shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-muted-foreground">
                Queue Total
              </p>
              <h3 className="text-2xl font-bold text-foreground">
                {(data ?? []).length} dispatches
              </h3>
            </div>
            <FileText className="h-8 w-8 text-muted-foreground/40" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-muted-foreground">
                Selected
              </p>
              <h3 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {selected.length}
              </h3>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-500/30" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-muted-foreground">
                Filtered
              </p>
              <h3 className="text-2xl font-bold text-foreground">
                {rows.length}
              </h3>
            </div>
            <PlayCircle className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none overflow-hidden">
        <CardHeader className="border-b bg-muted/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold text-foreground">
            Queue for Invoicing
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search DC / customer..."
                className="pl-8 h-8 text-xs shadow-none w-[220px]"
              />
            </div>
            {selected.length > 0 && (
              <Badge className="bg-primary text-primary-foreground px-3 py-1 shadow-sm animate-in slide-in-from-right-4 duration-200">
                {selected.length} Selected
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 font-medium text-xs"
              disabled
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" /> Filter
            </Button>
            {selected.length > 0 ? (
              <Button
                onClick={() => generate(selected)}
                disabled={busyIds.length > 0}
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 font-bold shadow-md text-xs gap-2 text-white"
              >
                {busyIds.length > 0 ? (
                  "Processing..."
                ) : (
                  <>
                    <PlayCircle className="w-3.5 h-3.5" /> Generate Selected
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 font-bold text-xs"
                disabled
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export List
              </Button>
            )}
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border-t rounded-b-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-muted/50 border-b-2">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.length === rows.length}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r">
                  Dispatch ID
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r">
                  Customer
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r">
                  Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r">
                  Items
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    Loading queue…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    No dispatched DCs awaiting invoicing.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const isBusy = busyIds.includes(row.dc_id);
                return (
                  <tr
                    key={row.dc_id}
                    className={`${selected.includes(row.dc_id) ? "bg-primary/5" : "even:bg-muted/30 hover:bg-muted/50"} border-b transition-all`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r align-middle">
                      <Checkbox
                        checked={selected.includes(row.dc_id)}
                        onCheckedChange={() => toggleOne(row.dc_id)}
                        disabled={isBusy}
                      />
                    </td>
                    <td
                      className="px-4 py-3.5 text-sm border-r font-mono font-bold text-primary hover:underline cursor-pointer align-middle"
                      onClick={() => navigate(`/delivery_challans/${row.dc_id}/show`)}
                    >
                      {row.dc_reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r font-semibold align-middle">
                      {row.customer_name ?? "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r text-muted-foreground align-middle">
                      {fmtDate(row.challan_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r font-black text-foreground align-middle">
                      {row.item_count}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Button
                        disabled={isBusy}
                        onClick={() => generate([row.dc_id])}
                        size="sm"
                        variant="secondary"
                        className="h-8 font-bold text-xs shadow-sm transition-all hover:bg-emerald-600 hover:text-white"
                      >
                        {isBusy ? "Creating…" : "Generate Invoice"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>
    </div>
  );
};
